import axios from 'axios'
import { getSavedState, saveState, removeState } from '@state/helpers'
import { transform as transformUser } from '@src/api/transformers/userTransformer'
import { AUTH_EXPIRED_MESSAGE } from '@utils/auth-session'
import { initQualtricsIntercept } from '@utils/qualtrics-intercept'

export const state = {
  currentUser: {},
  features: getSavedState('user.features'),
  userEmail: getSavedState('user.email'),
  userId: getSavedState('user.id'),
  seenSurveys: getSavedState('user.seenSurveys'),
  stateToken: null,
}

export const getters = {
  currentUser(state) {
    return state.currentUser
  },

  stateToken(state) {
    return state.stateToken
  },

  hasFeature(state) {
    return (featureSlug) => state.features?.includes(featureSlug)
  },

  seenSurveys(state) {
    return state.seenSurveys ?? []
  },

  hasHomeFeature(state, getters) {
    return getters.hasFeature('frontend-mottocenter-home')
  },

  hasUserProfileFeature(state, getters) {
    return getters.hasFeature('user-profile-management')
  },

  hasMyInfoProfileFeature(state, getters) {
    return getters.hasFeature('user-profile-management')
  },

  hasSmsPromptsFeature(state, getters) {
    return getters.hasFeature('sms-prompts')
  },

  isMottoCorporate(state) {
    return state.currentUser?.group?.accountTypeLabel === 'Motto Corporate'
  },

  isMottoMembership(state) {
    return state.currentUser?.group?.accountTypeLabel === 'Motto Membership'
  },

  canImpersonate(state, getters) {
    return getters.hasFeature('impersonate-user')
  },

  hasPersonnelFeature(state, getters) {
    return getters.hasFeature('personnel-management-frontend')
  },

  hasLoaPipelineFeature(state, getters) {
    return getters.hasFeature('loa-pipeline-access')
  },

  hasMyTechFeature(state, getters) {
    return getters.hasFeature('my-tech-session')
  },

  hasBirdeyeFeature(state, getters) {
    return getters.hasFeature('birdeye-integration-tile-visible')
  },

  canManagePersonnel(state) {
    const groups = state.currentUser?.groups?.map((x) => x.label) || []
    return groups.includes('Broker Owner') || groups.includes('Operations Manager')
  },

  canManageNetworkConnect(state) {
    const groups = state.currentUser?.groups?.map((x) => x.label) || []
    return (
      groups.includes('Broker Owner') ||
      groups.includes('Operations Manager') ||
      (groups.includes('Loan Originator') && state.currentUser?.isLoPhaseTwo)
    )
  },

  canAccessEducation(state, getters) {
    return getters.hasFeature('education-mottocenter') && !getters.isMottoCorporate
  },

  canAccessEducationAsManager(state, getters) {
    return getters.hasFeature('education-mottocenter') && getters.isBoOrOpsManager
  },

  canAccessEducationDemo(state, getters) {
    if (process.env.APP_ENV !== 'prod') {
      return true
    }

    return getters.hasFeature('education-mottocenter') && getters.isMottoCorporate
  },

  canAccessOnlinePresence(state, getters) {
    return getters.hasFeature('online-presence')
  },

  hasNetworkMyInfoTab(state, getters) {
    return getters.hasFeature('network-connect-v2')
  },

  hasNetworkConnectBadges(state, getters) {
    return getters.hasFeature('network-connect-v2')
  },

  canAccessOnboarding(state, getters) {
    return getters.hasFeature('onboarding-enabled-mottocenter')
  },

  isBoOrOpsManager(state) {
    const groups = state.currentUser?.groups?.map((x) => x.label) || []
    return groups.includes('Broker Owner') || groups.includes('Operations Manager')
  },

  shouldInitLogRocket(state, getters, rootState, rootGetters) {
    if (!rootGetters['auth/loggedIn']) {
      return false
    }

    const blockedEmails = ['mottomonitoring@mottomortgage.com']
    const userEmail = state.userEmail || ''
    const isValid = !blockedEmails.includes(userEmail.toLowerCase())

    if (process.env.APP_ENV === 'prod' && isValid) {
      return true
    }

    return process.env.APP_ENV === 'prod' || process.env.APP_ENV === 'qa'
  },
}

export const mutations = {
  SET_CURRENT_USER(state, user) {
    state.currentUser = user
    state.features = user?.features
    state.userEmail = user?.primaryEmail
    state.userId = user?.id
    saveState('user.features', user?.features)
    saveState('user.email', user?.primaryEmail)
    saveState('user.id', user?.id)
  },

  SET_STATE_TOKEN(state, newValue) {
    state.stateToken = newValue
  },

  SET_SEEN_SURVEYS(state, surveyId) {
    const oldSurveys = state.seenSurveys ? [...state.seenSurveys] : []
    state.seenSurveys = [...oldSurveys.filter((x) => x !== surveyId), surveyId]
    saveState('user.seenSurveys', [...oldSurveys.filter((x) => x !== surveyId), surveyId])
  },

  REMOVE_SEEN_SURVEYS() {
    removeState('user.seenSurveys')
  },

  SET_SEEN_SURVEYS_TIME(state, value) {
    saveState('user.seenSurveysTime', value)
  },
}

export const actions = {
  getMyUser({ commit, rootGetters }) {
    if (!rootGetters['auth/loggedIn']) {
      return
    }
    return axios
      .get(`/user`)
      .then((response) => {
        const user = transformUser(response.data)
        commit('SET_CURRENT_USER', user)
        initQualtricsIntercept(user.id)
        return response.data
      })
      .catch(() => {
        commit('auth/SET_ADMIN_AUTH_ERROR', AUTH_EXPIRED_MESSAGE, { root: true })
      })
  },
}
